import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";

const CATEGORY_MAP = {
  LOGISTICS: { label: "Logistics", colorIndex: 0 },
  MOTIVATION: { label: "Motivation", colorIndex: 1 },
  BACKGROUND_VERIFICATION: { label: "Background Verification", colorIndex: 2 },
  MUST_HAVE: { label: "Must-Have", colorIndex: 3 },
};

// --- Helper to get question text with consistent fallback ---
const getQuestionText = (question) =>
  question.que_text || question.question_text || question.question || "Question unavailable";

// --- Accordion wrapper for collapsible sections inside a card ---
const NestedSection = ({ title, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  const id = `section-${title.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <div className="rounded-lg border border-border-primary overflow-hidden">
      <Button
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
        aria-controls={id}
        className="w-full flex items-center rounded-none justify-between px-4 py-2.5 text-xs font-semibold text-text-secondary uppercase tracking-wide bg-background-tertiary/30 hover:bg-white/3 transition-colors"
      >
        <span>{title}</span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </Button>
      {open && (
        <div id={id} className="px-4 py-3 bg-background-secondary/70 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
};

const renderList = (items) => {
  if (!items?.length) return null;
  return (
    <ul className="space-y-1.5">
      {items.map((item, i) => (
        <li key={item.id || item.question_text || i} className="flex gap-2 text-xs md:text-sm text-text-primary">
          <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-indigo-400" />
          {typeof item === "string" ? item : item.question_text || item.text || JSON.stringify(item)}
        </li>
      ))}
    </ul>
  );
};

// --- Experience Based Card ---
const ExperienceBasedCard = ({ question, globalIndex }) => {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = question.question_intent || question.expected_answer_points?.length > 0 || question.refinement_notes;

  return (
    <div className="rounded-xl bg-background-tertiary/30 overflow-hidden border border-border-primary">
      <div className="px-5 py-4">
        <div className="flex gap-3">
          <span className="hidden mt-0.5 shrink-0 md:flex items-center justify-center w-6 h-6 rounded-full border border-border-primary font-semibold text-xs text-text-primary">
            {globalIndex + 1}
          </span>
          <div className="flex-1">
            <p className="text-xs md:text-sm text-text-primary leading-6">{getQuestionText(question)}</p>
            {question.target_competency && (
              <div className="mt-2.5">
                <Badge label={question.target_competency} colorIndex={0} variant="gradient" className="rounded-full text-[10px]" />
              </div>
            )}
          </div>
        </div>
      </div>

      {hasDetails && (
        <>
          <Button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="w-full flex items-center gap-2 px-5 py-2.5 border-t rounded-none border-border-primary text-xs text-text-secondary hover:text-text-primary transition-colors"
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            <span>{expanded ? "Hide intent & answer guide" : "Show intent & answer guide"}</span>
          </Button>

          {expanded && (
            <div className="px-5 pb-5 space-y-4 border-t border-border-primary pt-4">
              {question.question_intent && <SectionBlock title="Intent" text={question.question_intent} />}
              {question.expected_answer_points?.length > 0 && <SectionBlock title="Expected Answer Points" list={question.expected_answer_points} />}
              {question.refinement_notes && <SectionBlock title="Notes" text={question.refinement_notes} italic />}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// --- Reusable section block ---
const SectionBlock = ({ title, text, list, italic = false, bulletColor = "bg-indigo-400" }) => (
  <div>
    <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-1.5">{title}</p>
    {text && <p className={`md:text-sm text-xs text-text-primary leading-6 ${italic ? "italic" : ""}`}>{text}</p>}
    {list?.length > 0 && (
      <ul className="space-y-1">
        {list.map((item, i) => (
          <li key={item.id || item.question_text || i} className="flex gap-2 text-xs md:text-sm text-text-primary">
            <span className={`mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full ${bulletColor}`} />
            {typeof item === "string" ? item : item.question_text || item.text || JSON.stringify(item)}
          </li>
        ))}
      </ul>
    )}
  </div>
);

// --- Screening card ---
const ScreeningCard = ({ question, globalIndex }) => {
  const category = CATEGORY_MAP[question.category] ?? { label: question.category ?? "General", colorIndex: 0 };
  return (
    <div className="rounded-xl bg-background-tertiary/30 overflow-hidden border border-border-primary">
      <div className="px-5 py-4">
        <div className="flex gap-3">
          <span className="hidden mt-0.5 shrink-0 md:flex items-center justify-center w-6 h-6 rounded-full border border-border-primary font-semibold text-xs text-text-primary">
            {globalIndex + 1}
          </span>
          <div className="flex-1 space-y-3">
            <p className="text-xs md:text-sm text-text-primary leading-6">{getQuestionText(question)}</p>

            {question.category && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-text-secondary tracking-wide">Category:</span>
                <Badge label={category.label} colorIndex={category.colorIndex} variant="gradient" className="rounded-full text-[10px]" badgeStyle="text-[10px]" />
              </div>
            )}

            {question.why_asking && (
              <div className="rounded-lg bg-background-tertiary/30 border border-border-primary px-2 py-1">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold text-text-secondary tracking-wide whitespace-nowrap">Purpose:</p>
                  <p className="text-xs text-text-primary leading-6">{question.why_asking}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Motivational card ---
const MotivationalCard = ({ question, globalIndex }) => {
  return (
    <div className="rounded-xl bg-background-tertiary/30 overflow-hidden border border-border-primary">
      <div className="px-5 py-4">
        <div className="flex gap-3">
          <span className="hidden mt-0.5 shrink-0 md:flex items-center justify-center w-6 h-6 rounded-full border border-border-primary font-semibold text-xs text-text-primary">
            {globalIndex + 1}
          </span>
          <div className="flex-1 space-y-3">
            <p className="text-xs md:text-sm text-text-primary leading-6">{getQuestionText(question)}</p>
            {question.listen_for?.length > 0 && <SectionBlock title="Answer Points" list={question.listen_for} />}
            {question.why_asking && <SectionBlock title="Why Asking" text={question.why_asking} />}
            {question.red_flags?.length > 0 && <SectionBlock title="Red Flags" list={question.red_flags} bulletColor="bg-red-400" />}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Domain Specific Card with nested accordions + badges ---
const DomainSpecificCard = ({ question, globalIndex }) => {
  return (
    <div className="rounded-xl bg-background-tertiary/30 overflow-hidden border border-border-primary">
      <div className="px-5 py-4">
        <div className="flex gap-3">
          <span className="hidden mt-0.5 shrink-0 md:flex items-center justify-center w-6 h-6 rounded-full border border-border-primary font-semibold text-xs text-text-primary">
            {globalIndex + 1}
          </span>
          <div className="flex-1 space-y-3">
            <p className="text-xs md:text-sm text-text-primary leading-6">{getQuestionText(question)}</p>
            {question.topic && <Badge label={question.topic} colorIndex={globalIndex % 9} variant="gradient" className="rounded-full text-[10px] md:text-sm" />}

            {question.resume_evidence?.length > 0 && <SectionBlock title="Resume Evidence" list={question.resume_evidence} />}

            <div className="space-y-2">
              {question.follow_up_probes?.length > 0 && <NestedSection title="Follow-up Questions">{renderList(question.follow_up_probes)}</NestedSection>}
              {question.expected_answer_outline?.length > 0 && (
                <NestedSection title="Expected Answer Outline">{renderList(question.expected_answer_outline)}</NestedSection>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Leadership Card ---
const LeadershipCard = ({ question, globalIndex }) => {
  return (
    <div className="rounded-xl bg-background-tertiary/30 overflow-hidden border border-border-primary">
      <div className="px-5 py-4">
        <div className="flex gap-3">
          <span className="hidden mt-0.5 shrink-0 md:flex items-center justify-center w-6 h-6 rounded-full border border-border-primary font-semibold text-xs text-text-primary">
            {globalIndex + 1}
          </span>
          <div className="flex-1 space-y-3">
            <p className="text-xs md:text-sm text-text-primary leading-6">{getQuestionText(question)}</p>
            <div className="space-y-2">
              {question.follow_up_probes?.length > 0 && <NestedSection title="Follow-up Questions">{renderList(question.follow_up_probes)}</NestedSection>}
              {question.listen_for?.length > 0 && <SectionBlock title="Answer Points" list={question.listen_for} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Situational Case card ---
const SituationalCaseCard = ({ question, globalIndex }) => {
  return (
    <div className="rounded-xl bg-background-tertiary/30 overflow-hidden border border-border-primary">
      <div className="px-5 py-4">
        <div className="flex gap-3">
          <span className="hidden mt-0.5 shrink-0 md:flex items-center justify-center w-6 h-6 rounded-full border border-border-primary font-semibold text-xs text-text-primary">
            {globalIndex + 1}
          </span>
          <div className="flex-1 space-y-3">
            <p className="text-xs md:text-sm text-text-primary leading-6">{getQuestionText(question)}</p>
            <div className="space-y-2">
              {question.red_flags?.length > 0 && <SectionBlock title="Red Flags" list={question.red_flags} bulletColor="bg-red-400" />}
              {question.evaluation_criteria?.length > 0 && <NestedSection title="Evaluation Criteria">{renderList(question.evaluation_criteria)}</NestedSection>}
              {question.expected_reasoning_steps?.length > 0 && (
                <NestedSection title="Expected Reasoning Steps">{renderList(question.expected_reasoning_steps)}</NestedSection>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TYPE_CARD_MAP = {
  EXPERIENCE_BASED: ExperienceBasedCard,
  SCREENING: ScreeningCard,
  MOTIVATIONAL: MotivationalCard,
  DOMAIN_SPECIFIC: DomainSpecificCard,
  LEADERSHIP: LeadershipCard,
  SITUATIONAL_CASE: SituationalCaseCard,
};

const QuestionCard = ({ question, globalIndex, localIndex, type = "EXPERIENCE_BASED" }) => {
   if (!question || typeof question !== "object") return null;
  const CardComponent = TYPE_CARD_MAP[type] || ExperienceBasedCard;
  return <CardComponent question={question} globalIndex={globalIndex} />;
};

export default QuestionCard;